import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BuildingIcon, GlobeIcon, MapPinIcon, TagIcon } from '@phosphor-icons/react';
import Tooltip from '@/components/ui/Tooltip';
import { twMerge } from 'tailwind-merge';
import Popover from '@/components/ui/Popover';

export type TagPillGroup = 'document_type' | 'domain' | 'site' | 'national_returns';

const GROUP_ICON = {
  document_type: <TagIcon size={12} weight="light" className="shrink-0" />,
  domain: <GlobeIcon size={12} weight="light" className="shrink-0" />,
  site: <MapPinIcon size={12} weight="light" className="shrink-0" />,
  national_returns: <BuildingIcon size={12} weight="light" className="shrink-0" />
} as const;

const PILL_TONE = {
  confirmed: 'bg-gray-100 ring-gray-200',
  suggested: 'bg-white ring-gray-200'
} as const;

export function TagPill({
  label,
  group,
  confirmed = true,
  icon,
  className,
  title,
  trailing,
  onMouseEnter,
  onMouseLeave,
  tooltipDelay
}: {
  label: string;
  group?: TagPillGroup;
  confirmed?: boolean;
  icon?: ReactNode;
  className?: string;
  title?: ReactNode;
  trailing?: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  tooltipDelay?: number;
}) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  const resolvedIcon = icon ?? (group ? GROUP_ICON[group] : null);
  const hint = title ?? (confirmed === false ? 'AI-suggested — open the document to review' : '');
  const tooltip = truncated ? label : hint;

  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el) return;
    const check = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    check();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [label]);

  const pill = (
    <span
      className={twMerge(
        'inline-flex min-w-0 max-w-full font-medium items-center gap-1 rounded-full px-2 py-0.5 text-xs ring-1 ring-inset',
        confirmed
          ? twMerge('text-content-subtle', PILL_TONE.confirmed)
          : twMerge('text-content-subtlest', PILL_TONE.suggested),
        className
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {resolvedIcon}
      <span ref={labelRef} className="truncate">
        {label}
      </span>
      {trailing}
    </span>
  );

  if (!tooltip) return pill;

  return (
    <Tooltip title={tooltip} placement="top" delay={tooltipDelay ?? (confirmed === false ? 1 : undefined)}>
      {pill}
    </Tooltip>
  );
}

export type TagPillItem = {
  key: string;
  label: string;
  group?: TagPillGroup;
  confirmed?: boolean;
  icon?: ReactNode;
  className?: string;
  title?: string;
};

const LINE_GAP = 4;

const GROUP_SORT_ORDER: Record<TagPillGroup, number> = {
  document_type: 0,
  domain: 1,
  site: 2,
  national_returns: 3
};

function compareTagPillItems(a: TagPillItem, b: TagPillItem): number {
  const groupA = a.group == null ? -1 : (GROUP_SORT_ORDER[a.group] ?? 99);
  const groupB = b.group == null ? -1 : (GROUP_SORT_ORDER[b.group] ?? 99);
  if (groupA !== groupB) return groupA - groupB;
  return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
}

function countPillsOnLines(widths: number[], containerWidth: number, maxLines: number): number {
  if (containerWidth <= 0 || widths.length === 0 || maxLines <= 0) return 0;
  let lines = 1;
  let used = 0;
  let count = 0;
  for (const width of widths) {
    const extra = used > 0 ? LINE_GAP + width : width;
    if (used + extra <= containerWidth) {
      used += extra;
      count += 1;
      continue;
    }
    if (lines >= maxLines) break;
    lines += 1;
    used = width;
    count += 1;
  }
  return count;
}

function countPillsThatFit(widths: number[], containerWidth: number, maxLines: number): number {
  if (widths.length === 0) return 0;
  if (containerWidth <= 0) return Math.min(widths.length, maxLines);

  if (countPillsOnLines(widths, containerWidth, maxLines) === widths.length) {
    return widths.length;
  }

  // Last line is reserved for one truncated pill + the +N badge.
  const packed = countPillsOnLines(widths, containerWidth, Math.max(maxLines - 1, 0));
  if (packed >= widths.length) return widths.length;
  return packed + 1;
}

const overflowBadgeClassName =
  'inline-flex shrink-0 cursor-default items-center rounded-full bg-surface-subtle px-2 py-0.5 text-xs text-content-subtlest ring-1 ring-inset ring-border-subtle';

export function TagPillOverflow({
  items,
  maxVisible = 2,
  maxWidth,
  className
}: {
  items: TagPillItem[];
  maxVisible?: number;
  maxWidth?: number | string;
  className?: string;
}) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
  const [visibleCount, setVisibleCount] = useState(() => Math.min(items.length, maxVisible));
  const overflowCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const sortedItems = useMemo(() => [...items].sort(compareTagPillItems), [items]);

  const openOverflow = () => {
    if (overflowCloseTimer.current) clearTimeout(overflowCloseTimer.current);
    setOverflowOpen(true);
  };
  const closeOverflow = () => {
    overflowCloseTimer.current = setTimeout(() => setOverflowOpen(false), 120);
  };

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const children = Array.from(measure.children) as HTMLElement[];
    if (children.length < 2) return;
    const widths = children.slice(0, -1).map((child) => child.offsetWidth);
    setVisibleCount(countPillsThatFit(widths, container.clientWidth, maxVisible));
  }, [maxVisible, sortedItems]);

  useLayoutEffect(() => {
    recompute();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [recompute]);

  if (sortedItems.length === 0) return null;

  const visible = sortedItems.slice(0, visibleCount);
  const hidden = sortedItems.slice(visibleCount);
  const lastVisibleIndex = visible.length - 1;

  const render = (item: TagPillItem) => (
    <TagPill
      key={item.key}
      label={item.label}
      group={item.group}
      confirmed={item.confirmed}
      icon={item.icon}
      className={item.className}
      title={item.title}
    />
  );

  const overflow = hidden.length > 0 && (
    <>
      <span
        ref={setOverflowAnchor}
        onMouseEnter={openOverflow}
        onMouseLeave={closeOverflow}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        className={overflowBadgeClassName}
      >
        +{hidden.length}
      </span>
      <Popover
        open={overflowOpen}
        anchorEl={overflowAnchor}
        onClose={() => setOverflowOpen(false)}
        placement="top"
        showArrow
        onMouseEnter={openOverflow}
        onMouseLeave={closeOverflow}
        className="z-critical max-w-[240px] p-2"
      >
        <div className="flex flex-col items-start gap-1">
          {hidden.map((item) => (
            <TagPill
              key={item.key}
              label={item.label}
              group={item.group}
              confirmed={item.confirmed}
              icon={item.icon}
              className={item.className}
              title=""
            />
          ))}
        </div>
      </Popover>
    </>
  );

  return (
    <div
      ref={containerRef}
      className={twMerge(
        'relative flex w-full min-w-0 items-center gap-1 overflow-hidden',
        maxVisible <= 1 ? 'flex-nowrap' : 'flex-wrap',
        className
      )}
      style={maxWidth !== undefined ? { maxWidth } : undefined}
    >
      <div
        ref={measureRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex h-0 w-0 items-center gap-1 overflow-hidden whitespace-nowrap"
      >
        {sortedItems.map((item) => (
          <span key={item.key} className="shrink-0">
            <TagPill
              label={item.label}
              group={item.group}
              confirmed={item.confirmed}
              icon={item.icon}
              className={item.className}
              title=""
            />
          </span>
        ))}
        <span className={overflowBadgeClassName}>+99</span>
      </div>
      {visible.map((item, index) => {
        const isLast = index === lastVisibleIndex;
        if (isLast && overflow) {
          return (
            <span
              key={item.key}
              className={twMerge(
                'flex min-w-0 flex-nowrap items-center gap-1',
                maxVisible <= 1 ? 'max-w-full flex-1' : 'w-full max-w-full'
              )}
            >
              <span className="min-w-0 overflow-hidden">{render(item)}</span>
              {overflow}
            </span>
          );
        }
        return (
          <span key={item.key} className="max-w-full min-w-0 shrink-0 overflow-hidden">
            {render(item)}
          </span>
        );
      })}
    </div>
  );
}
